// ═══════════════════════════════════════════════════════════════════════════
// Guardia de acceso — Etapa 3
//
// Decide qué se muestra: pantalla de acceso o la aplicación.
//
// El arranque NO depende de la red. Se lee la sesión de localStorage y, si
// existe, se entra de inmediato con la membresía cacheada; la verificación
// contra el servidor ocurre después, en segundo plano, y solo si hay conexión.
// Al revés — bloquear hasta confirmar con el servidor — cada zona sin señal
// sería una jornada perdida.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import { Login } from "./Login.jsx";
import { CanjearTicket } from "./CanjearTicket.jsx";
import { EntrarConTicket } from "./EntrarConTicket.jsx";
import { PrimerosPasos } from "./PrimerosPasos.jsx";
import { PedirRecuperacion, PonerContrasena } from "./Contrasena.jsx";
import { ticketDeLaLiga, limpiarLiga } from "./ticket.js";
import { sesionLocal, cargarMembresia, membresiaCacheada, alCambiarSesion, esRecuperacion } from "./sesion.js";
import { LOGO_BASE64 } from "../pdf/logo.js";

export function Guardia({ children }) {
  const [estado, setEstado] = useState("cargando");   // cargando | fuera | sin-vinicola | dentro
  const [sesion, setSesion] = useState(null);
  const [membresia, setMembresia] = useState(null);
  // El código se lee UNA vez y se borra de la barra de direcciones en el acto:
  // ya está en memoria y dejarlo ahí solo lo expone en el historial.
  // Venir de un enlace de recuperación se detecta ANTES de que el cliente de
  // Supabase consuma el fragmento; si no, la app entraría normal y el usuario
  // seguiría sin saber su contraseña — que es exactamente lo que pasó.
  const [recuperando, setRecuperando] = useState(() => esRecuperacion());
  const [pidiendo, setPidiendo] = useState(null);   // correo tecleado, o null
  const [prefiereCuenta, setPrefiereCuenta] = useState(false);
  // Quien acaba de entrar con un ticket: falta decirle su nombre y darle su
  // llave. Vive en memoria y solo para esta vez.
  const [reciente, setReciente] = useState(null);
  const [ticket] = useState(() => {
    const t = ticketDeLaLiga();
    if (t) limpiarLiga();
    return t;
  });

  useEffect(() => {
    let vivo = true;

    (async () => {
      const s = await sesionLocal();          // sin red
      if (!vivo) return;
      if (!s) { setEstado("fuera"); return; }

      // Se entra con lo que ya se sabe; el servidor se consulta después.
      const cacheada = membresiaCacheada();
      setSesion(s);
      setMembresia(cacheada);
      setEstado("dentro");

      const fresca = await cargarMembresia(s.user.id);
      if (!vivo) return;
      if (fresca) {
        setMembresia(fresca);
        // Traer un ticket teniendo ya acceso es el caso de quien trabaja para
        // una vinícola y lo invitan a otra: se le ofrece canjearlo.
        if (ticket) setEstado("sin-vinicola");
      } else if (navigator.onLine && !cacheada) {
        // Con red y sin membresía: la sesión es válida pero la cuenta no
        // pertenece a ninguna vinícola. Antes se la sacaba de aquí, y ese es
        // exactamente el estado en el que llega un vendedor nuevo — sacarlo
        // lo dejaba sin manera de entrar nunca. Ahora se le pide su ticket.
        setEstado("sin-vinicola");
      }
    })();

    const desuscribir = alCambiarSesion((evento, s) => {
      if (!vivo) return;
      if (evento === "SIGNED_OUT" || !s) {
        setSesion(null); setMembresia(null); setEstado("fuera");
      } else {
        setSesion(s);
      }
    });

    return () => { vivo = false; desuscribir?.(); };
  }, []);

  if (estado === "cargando") {
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", background: "#0a0a0a", gap: 14 }}>
        <img src={LOGO_BASE64} alt="" style={{ width: 64, height: 64, borderRadius: 10,
          objectFit: "cover", opacity: 0.85 }} />
        <div style={{ color: "#555", fontSize: 12 }}>Abriendo…</div>
      </div>
    );
  }

  if (pidiendo !== null) {
    return <PedirRecuperacion correoInicial={pidiendo} onVolver={() => setPidiendo(null)} />;
  }

  // La sesión de recuperación es válida pero sirve para UNA cosa: poner la
  // contraseña nueva. No se entra a la app hasta que se haya puesto.
  if (recuperando && sesion) {
    return <PonerContrasena sesion={sesion} onListo={async () => {
      setRecuperando(false);
      const fresca = await cargarMembresia(sesion.user.id);
      setMembresia(fresca);
      setEstado(fresca ? "dentro" : "sin-vinicola");
    }} />;
  }

  // ── Con ticket y sin sesión: el aparato ENTRA, no crea cuenta ────────────
  //
  // Es la mitad que le faltaba al QR. Antes esto caía en el formulario de
  // correo y contraseña, así que apuntar la cámara no alcanzaba: había que
  // crearse una cuenta en una máquina prestada.
  //
  // `prefiereCuenta` deja el camino viejo a un clic, para quien ya trabaja con
  // otra vinícola y lo invitan a una segunda: ese quiere su identidad, no un
  // aparato nuevo.
  if (estado === "fuera" && ticket && !prefiereCuenta) {
    return <EntrarConTicket codigo={ticket}
      onPrefiereCuenta={() => setPrefiereCuenta(true)}
      onEntro={async ({ sesion: s, tenant }) => {
        setSesion(s);
        const fresca = await cargarMembresia(s.user.id);
        setMembresia(fresca);
        // No entra directo: primero su nombre y su llave. Es el único momento
        // en que está mirando a propósito — diez minutos después está
        // levantando un pedido y no va a ir a buscarlas.
        if (fresca) setReciente({ tenant });
        setEstado(fresca ? "dentro" : "sin-vinicola");
      }} />;
  }

  if (estado === "fuera") {
    return <Login ticket={ticket} onRecuperar={(correo) => setPidiendo(correo || "")}
      onEntrar={({ session, membresia }) => {
      setSesion(session);
      setMembresia(membresia);
      // Iniciar sesión sin pertenecer a nada lleva al canje, no adentro.
      // Con ticket en mano se va al canje aunque ya tenga acceso a otra.
      setEstado(membresia && !ticket ? "dentro" : "sin-vinicola");
    }} />;
  }

  if (estado === "sin-vinicola") {
    return <CanjearTicket sesion={sesion} codigoInicial={ticket}
      yaTeniaAcceso={!!membresia}
      alEntrar={async () => {
        const fresca = await cargarMembresia(sesion.user.id);
        setMembresia(fresca);
        setEstado(fresca ? "dentro" : "sin-vinicola");
      }}
      alCancelar={membresia ? () => setEstado("dentro") : null} />;
  }

  if (reciente && sesion && membresia) {
    return <PrimerosPasos sesion={sesion} tenant={reciente.tenant}
      onListo={() => setReciente(null)} />;
  }

  return children({ sesion, membresia });
}
